/**
 * What a seat is called, in one place (D-139).
 *
 * This rule was written for the ticket face at D-117 and lived in the API's
 * artifact renderer, so the PDF said "Table 30 · Seat 3" while the cart and the
 * seat picker said "Table · Seat 3" — the section's name and the seat number,
 * with the one number the customer needs missing. A room whose section is
 * literally called "Table" shows that especially badly.
 *
 * Every surface that names a seat now calls this, so the cart, the checkout,
 * the ticket, the wallet pass and the box office cannot drift apart again.
 */

/**
 * A section whose whole name is "Table" says nothing once the table itself is
 * named. A room with one table section calls it exactly that, and
 * "Table · Table 5 · Seat 1" is how a label stops being read.
 */
const SAYS_NOTHING_BESIDE_A_TABLE = /^tables?$/i;

export interface SeatLabelParts {
  section: string | null;
  table: string | null;
  row: string | null;
  seat: string | null;
  /** A general admission area stands in for all of the above. */
  ga_area: string | null;
}

/** "Table 30 · Seat 3", "Floor A · Row C · Seat 12", "Lawn". */
export function seatLabel(parts: SeatLabelParts): string {
  if (parts.ga_area) return parts.ga_area;
  const table = parts.table ? `Table ${parts.table}` : null;
  const keepSection = parts.section && !(table && SAYS_NOTHING_BESIDE_A_TABLE.test(parts.section));
  const pieces = [keepSection ? parts.section : null, table, parts.row ? `Row ${parts.row}` : null, parts.seat ? `Seat ${parts.seat}` : null].filter(Boolean);
  // Something is always better than nothing: a seat with no labels at all is
  // still a seat somebody paid for and has to be able to point at.
  return pieces.length > 0 ? pieces.join(' · ') : 'General admission';
}

/**
 * The order seats belong in, in one place (D-216).
 *
 * The cart listed seats in the order they were held, which for best-available
 * is whatever the allocator happened to pick — so a table read "Seat 6, Seat
 * 3, Seat 4, Seat 2, Seat 1, Seat 5" and a customer checking they had the right
 * six had to read every line. The first performance in a package looked right
 * and the rest did not, which is exactly what an unsorted list looks like.
 *
 * The label comparison itself is `compareLabels` from `best-available`, already
 * written to read numbers as numbers so table 2 comes before table 10, and
 * hand-rolled rather than `localeCompare` so a Durable Object and a Worker
 * cannot disagree. This only adds the missing part — what to do about a seat
 * that has no row, or no table — and the order to apply them in.
 */
import { compareLabels } from './best-available';

export interface SeatOrderParts {
  section: string | null;
  table: string | null;
  row: string | null;
  seat: string | null;
}

/** Nothing sorts after something: a seat with no row is not "row zero". */
function byLabel(a: string | null, b: string | null): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return compareLabels(a, b);
}

/** Section, then table, then row, then seat — the order somebody reads a ticket in. */
export function compareSeats(a: SeatOrderParts, b: SeatOrderParts): number {
  return byLabel(a.section, b.section) || byLabel(a.table, b.table) || byLabel(a.row, b.row) || byLabel(a.seat, b.seat);
}
